import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { TERMINAL_JOB_STATUSES } from '../../../../../models/KeepInTouchAgent';
import {
	invalidateKeepInTouchJob,
	invalidateKeepInTouchScores,
	useKeepInTouchJob,
} from '../../../../../queries/KeepInTouchAgent';
import { usePollingRefresh } from '../../../../../hooks/usePollingRefresh';
import { styleSheet } from './styles';

interface JobProgressProps {
	jobId: string;
	configId: string;
	onComplete?: () => void;
}

export const JobProgress: React.FC<JobProgressProps> = ({ jobId, configId, onComplete }) => {
	const { data: job } = useKeepInTouchJob({ jobId });
	const isTerminal = job?.status && TERMINAL_JOB_STATUSES.includes(job.status);
	const onCompleteRef = React.useRef(onComplete);
	onCompleteRef.current = onComplete;
	const handledStatusRef = React.useRef<string | null>(null);

	const refreshFn = React.useCallback(() => {
		if (!isTerminal) {
			invalidateKeepInTouchJob(jobId);
		}
	}, [jobId, isTerminal]);

	usePollingRefresh(refreshFn);

	React.useEffect(() => {
		if (!job?.status || !TERMINAL_JOB_STATUSES.includes(job.status)) return;
		if (handledStatusRef.current === job.status) return;
		handledStatusRef.current = job.status;

		invalidateKeepInTouchScores(configId);

		if (job.status === 'Complete') {
			toast.success('Research complete');
		} else if (job.status === 'PartialSuccess') {
			toast.info('Research completed with some failures');
		} else if (job.status === 'Failed') {
			toast.error('Research failed');
		}

		onCompleteRef.current?.();
	}, [job?.status, configId]);

	if (!job) return null;

	const statusLabel =
		job.status === 'Queued'
			? 'Queued...'
			: job.status === 'InProgress'
				? 'Researching contacts...'
				: job.status === 'Complete'
					? 'Complete'
					: job.status === 'PartialSuccess'
						? 'Completed with errors'
						: 'Failed';

	return (
		<div className={css(styleSheet.container)}>
			<div className={css(styleSheet.header)}>
				<p className={css(styleSheet.statusText)}>{statusLabel}</p>
				<span className={css(styleSheet.percentText)}>{job.percentComplete}%</span>
			</div>
			<div className={css(styleSheet.progressBarOuter)}>
				<div className={css(styleSheet.progressBarInner)} style={{ width: `${job.percentComplete}%` }} />
			</div>
			{job.currentContactName ? (
				<p className={css(styleSheet.currentContact)}>Currently researching: {job.currentContactName}</p>
			) : null}
		</div>
	);
};
